import { useContext, useEffect, useMemo, useState } from 'react'
import { UIContext } from 'Context/UI'
import { TableContext } from 'Context/Table'
import { useNavigate } from 'react-router-dom'
import { useBillingTransactionStore } from 'Store/BillingTransaction'
import { useFileDownloadStore } from 'Store/FileDownload'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { useLang } from '_metronic/i18n/Metronici18n'
import moment from 'moment'
import 'moment-timezone'
import { extractFileName } from 'Utils/extractFileName'
import { generateDownloadUrl } from 'Utils/generateDownloadUrl'

// import CustomToolbar from '../Components/CustomToolbar'
import InvoiceCell from '../Components/Invoice'
import StatusCell from '../Components/StatusCell'
import formatNumberCommas from 'Utils/formatNumberCommas'
import Swal from 'sweetalert2'

const THIS_WEEK_START_DAY = process.env.REACT_APP_THIS_WEEK_START_DAY
	? parseInt(process.env.REACT_APP_THIS_WEEK_START_DAY)
	: 0
const THIS_WEEK_END_DAY = process.env.REACT_APP_THIS_WEEK_END_DAY
	? parseInt(process.env.REACT_APP_THIS_WEEK_END_DAY)
	: 6

const INITIAL_FILTER_STATE = {
	startDate: moment(THIS_WEEK_START_DAY, 'days').format('YYYY-MM-DD'),
	endDate: moment(THIS_WEEK_END_DAY, 'days').format('YYYY-MM-DD'),
	dateType: 'today' as 'today' | 'thisweek' | 'thismonth',
}

const INITIAL_BILLING_STATE = {
	startDate: moment(THIS_WEEK_START_DAY, 'days').format('YYYY-MM-DD'),
	endDate: moment(THIS_WEEK_END_DAY, 'days').format('YYYY-MM-DD'),
}

const ViewModel = () => {
	const navigate = useNavigate()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { updateError, updateLoading, updatePagination, updateDefaultSorting, updateSorting } =
		useContext(TableContext)
	const [currentTab, setCurrentTab] = useState(0)
	const { data, getAll, clearState } = useBillingTransactionStore(
		state => ({
			data: state.data,
			getAll: state.getAll,
			clearState: state.clearState,
		}),
		shallow
	)

	const [filterState, setFilterState] = useState(INITIAL_FILTER_STATE)
	const [billingState, setBillingState] = useState(INITIAL_BILLING_STATE)

	const downloadFile = useFileDownloadStore(state => state.download)
	const lang = useLang()

	const onChangeFilterState = (key: string, value: any) => {
		setFilterState(prevState => ({
			...prevState,
			[key]: value,
		}))
	}

	const onChangeBillingState = (key: string, value: any) => {
		setBillingState(prevState => ({
			...prevState,
			[key]: value,
		}))
	}

	const getWeekRangesOfMonth = (year: number, month: number) => {
		const weeks = []
		const startDate = moment([year, month])
		const endDate = moment(startDate).endOf('month')

		let startOfWeek = startDate.clone().startOf('week')
		let endOfWeek = startOfWeek.clone().endOf('week')

		while (startOfWeek < endDate) {
			// Ensure the end of the week is not beyond the month's end
			if (endOfWeek > endDate) {
				endOfWeek = endDate.clone()
			}

			// Ensure the start of the week is not before the month's start
			if (startOfWeek < startDate) {
				startOfWeek = startDate.clone()
			}

			weeks.push({
				start: startOfWeek.toISOString(),
				end: endOfWeek.toISOString(),
			})

			// Prepare for the next iteration
			startOfWeek.add(1, 'week')
			endOfWeek = startOfWeek.clone().endOf('week')
		}

		return weeks
	}

	const weekRanges = useMemo(() => {
		const now = moment() // Get the current date
		const currentYear = now.year()
		const currentMonth = now.month() // Month is 0-indexed (0 for January, 1 for February, etc.)
		return getWeekRangesOfMonth(currentYear, currentMonth)
	}, [])

	const setupTable = () => {
		updateError(false)
		updatePagination(true)
		updateDefaultSorting('date', true)
		updateSorting('date', true)
	}

	const fetchData = () => {
		updateLoading(true)
		updateError(false)
		getAll().then(({ success, data }) => {
			if (!success) {
				toast.error(data)
				updateError(true)
				updateLoading(false)
			} else {
				updateError(false)
				updateLoading(false)
			}
		})
	}

	const onDownload = (transactionUUID: string, type: 'paidEvidence' | 'receipt') => {
		// @ts-ignore
		const finded = data.find((item: any) => item.uuId === transactionUUID)
		if (!finded) return

		const filePath = type === 'paidEvidence' ? finded?.paidEvidenceFile : finded?.receiptFile
		if (!filePath) return

		downloadFile(
			generateDownloadUrl(filePath),
			extractFileName(generateDownloadUrl(filePath))
		).then(({ success, data }) => {
			if (!success) {
				Swal.fire({
					icon: 'error',
					title: lang === 'en' ? 'Download unsuccessfully' : 'ดาวน์โหลดไฟล์ไม่สำเร็จ',
					text: data,
				})
			}
		})
	}

	const TABLE_CONFIGS: any[] = [
		{
			Header: 'Invoice',
			accessor: 'invoice',
			width: 300,
			minWidth: 300,
			Cell: ({ value, row }: any) => <InvoiceCell value={value} row={row} />,
		},
		{
			Header: 'Billing Date',
			accessor: 'createdAt',
			width: 125,
			minWidth: 125,
			Cell: ({ value }: any) => (
				<div className="text-kumopack-grey-600">{moment(value).format('DD MMMM YYYY')}</div>
			),
		},
		{
			Header: 'Status',
			accessor: 'status',
			width: 50,
			minWidth: 50,
			Cell: ({ value }: any) => <StatusCell value={value} />,
		},
		{
			Header: 'Amount',
			headerRight: true,
			accessor: 'net',
			width: 50,
			minWidth: 50,
			Cell: ({ value }: any) => (
				<div className="text-end text-kumopack-grey-600">{formatNumberCommas(value)}</div>
			),
		},
		{
			Header: '',
			accessor: 'action',
			width: 300,
			minWidth: 300,
			Cell: ({ row }: any) => (
				<div className="d-flex flex-row">
					<div className="w-100">
						<button
							className="btn btn-sm btn-muted btn-active-light text-kumopack-secondary-700 text-start"
							onClick={() => onDownload(row.original.uuId, 'paidEvidence')}>
							{lang === 'en' ? 'Download Paid Evidence' : 'ดาวน์โหลดหลักฐานการชำระเงิน'}
						</button>
					</div>
					{row.original.term === 2 && row.original.receiptFile && (
						<div className="w-100">
							<button
								className="btn btn-sm btn-muted btn-active-light text-kumopack-secondary-700 text-start"
								onClick={() => onDownload(row.original.uuId, 'receipt')}>
								{lang === 'en' ? 'Download Receipt' : 'ดาวน์โหลดใบเสร็จรับเงิน'}
							</button>
						</div>
					)}
				</div>
			),
			disableSortBy: true,
		},
	]

	useEffect(() => {
		fetchData()

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (currentTab === 1) {
			navigate('/billing/invoicem')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTab])

	useEffect(() => {
		setupTable()
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		onChangeFilterState('startDate', moment(THIS_WEEK_START_DAY, 'days').format('YYYY-MM-DD'))
		onChangeFilterState('endDate', moment(THIS_WEEK_END_DAY, 'days').format('YYYY-MM-DD'))

		onChangeBillingState('startDate', moment(THIS_WEEK_START_DAY, 'days').format('YYYY-MM-DD'))
		onChangeBillingState('endDate', moment(THIS_WEEK_END_DAY, 'days').format('YYYY-MM-DD'))
	}, [])

	return {
		weekRanges,
		data: data,
		setCurrentTab,
		TABLE_CONFIGS,
		filterState,
		billingState,
		onChangeFilterState,
		onChangeBillingState,
	}
}

export default ViewModel
